/** @jsxImportSource @emotion/react */

import { useLazyQuery, useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import React, { FC, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { createTestimonial, createTestimonialVariables } from '__generatedTypes__/createTestimonial'
import { findAllPlatforms_findAllPlatforms_items as IPlatform } from '__generatedTypes__/findAllPlatforms'
import { findAllTestimonial, findAllTestimonialVariables } from '__generatedTypes__/findAllTestimonial'
import { CMSBoosterPages, TestimonialWhere } from '__generatedTypes__/globalTypes'
import { updateTestimonial, updateTestimonialVariables } from '__generatedTypes__/updateTestimonial'
import TabsNavigation from 'components/TabsNavigation'
import client from 'configs/apolloClient'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { CREATE_TESTIMONIAL, FIND_ALL_TESTIMONIALS, UPDATE_TESTIMONIAL } from 'queries/testimonials'
import { PlatformSlug, TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { Loader } from 'components/Loader'
import NotFound from 'components/NotFound'
import { toast } from 'utils/toast'
import { InputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { LandingPagesNames } from './consts'
import { isTestimonialPageAvailable } from './platformConfig'
import * as classes from './styles'
import TestimonialsCards from './TestimonialsCards'

interface IProps {
  platform: IPlatform
  fixedPage?: CMSBoosterPages
}

const Testimonials: FC<IProps> = ({ platform, fixedPage }) => {
  const tabs = Object.entries(LandingPagesNames)
    .map(([key, value]) => ({
      id: key,
      title: value,
    }))
    .filter((tab) => fixedPage || isTestimonialPageAvailable(platform.slug as PlatformSlug, tab.id as CMSBoosterPages))
  const [selectedPage, setSelectedPage] = useState(fixedPage ? { id: fixedPage, title: 'Fixed page' } : tabs[0])

  useEffect(() => {
    setSelectedPage(fixedPage ? { id: fixedPage, title: 'Fixed page' } : tabs[0])
  }, [fixedPage])

  const platformQuery: Partial<TestimonialWhere> =
    !selectedPage || selectedPage.id === CMSBoosterPages.HomePage
      ? {}
      : {
          platform: { id: platform.id },
        }

  const data = selectedPage
    ? client.readQuery<findAllTestimonial, findAllTestimonialVariables>({
        query: FIND_ALL_TESTIMONIALS,
        variables: {
          query: {
            where: {
              ...platformQuery,
              pageWithOrder: {
                page: selectedPage.id as CMSBoosterPages,
              },
            },
            take: TAKE_ALL_ITEMS_COUNT,
          },
        },
      })
    : null
  const testimonialInfo = data?.findAllTestimonial?.items[0]

  const isTestimonialsInfoFormHidden = selectedPage?.id === CMSBoosterPages.Review

  const initialValues = testimonialInfo || { title: '', description: '' }

  const [getTestimonials, { loading, called }] = useLazyQuery<findAllTestimonial, findAllTestimonialVariables>(
    FIND_ALL_TESTIMONIALS
  )

  const [updateTestimonial, { loading: updateLoading }] = useMutation<updateTestimonial, updateTestimonialVariables>(
    UPDATE_TESTIMONIAL
  )

  const [createTestimonial, { loading: createLoading }] = useMutation<createTestimonial, createTestimonialVariables>(
    CREATE_TESTIMONIAL
  )

  useEffect(() => {
    if (!selectedPage) {
      return
    }

    ;(async () => {
      try {
        getTestimonials({
          variables: {
            query: {
              where: {
                ...platformQuery,
                pageWithOrder: {
                  page: selectedPage.id as CMSBoosterPages,
                },
              },
              take: TAKE_ALL_ITEMS_COUNT,
            },
          },
        })
      } catch (error) {
        toast.error('Failed to load testimonials')
      }
    })()
  }, [selectedPage, platform])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = async (fieldValues: Record<string, any>) => {
    if (testimonialInfo) {
      try {
        const resp = await updateTestimonial({
          variables: {
            updateTestimonialInputFieldName: {
              id: testimonialInfo.id,
              title: fieldValues.title,
              description: fieldValues.description,
            },
          },
        })
        const newData = {
          id: resp.data?.updateTestimonial.id || '',
          __typename: resp.data?.updateTestimonial.__typename || 'TestimonialType',
          title: resp.data?.updateTestimonial.title,
          description: resp.data?.updateTestimonial.description,
          platform: {
            id: platform.id,
          },
          pageWithOrder: [{ page: selectedPage.id as CMSBoosterPages, order: 0 }],
        }
        client.writeQuery({
          query: FIND_ALL_TESTIMONIALS,
          variables: {
            query: {
              where: {
                ...platformQuery,
                pageWithOrder: { page: selectedPage.id as CMSBoosterPages },
              },
              take: TAKE_ALL_ITEMS_COUNT,
            },
          },
          data: {
            findAllTestimonial: { count: 1, items: [newData] },
          },
        })
        toast.success('Testimonial updated successfully')
      } catch (error) {
        toast.error('Failed to update testimonial')
      }
    } else {
      try {
        const resp = await createTestimonial({
          variables: {
            createTestimonialInput: {
              title: fieldValues.title,
              description: fieldValues.description,
              platform: {
                id: platform.id,
              },
              pageWithOrder: [{ page: selectedPage.id as CMSBoosterPages, order: 0 }],
              textFeedback: [],
            },
          },
        })
        const newData = {
          id: resp.data?.createTestimonial.id || '',
          __typename: resp.data?.createTestimonial.__typename || 'TestimonialType',
          title: resp.data?.createTestimonial.title,
          description: resp.data?.createTestimonial.description,
          platform: {
            id: platform.id,
          },
          pageWithOrder: [{ page: selectedPage.id as CMSBoosterPages, order: 0 }],
          textFeedback: [],
        }
        client.writeQuery({
          query: FIND_ALL_TESTIMONIALS,
          variables: {
            query: {
              where: {
                ...platformQuery,
                pageWithOrder: { page: selectedPage.id as CMSBoosterPages },
              },
              take: TAKE_ALL_ITEMS_COUNT,
            },
          },
          data: {
            findAllTestimonial: { count: 1, items: [newData] },
          },
        })
        toast.success('Testimonial created successfully')
      } catch (error) {
        toast.error('Failed to create testimonial')
      }
    }
  }

  if (!selectedPage) {
    return <NotFound />
  }

  return (
    <>
      {!fixedPage && (
        <div>
          <Typography variant="body2" css={classes.label}>
            Page
          </Typography>
          <TabsNavigation tabs={tabs} value={selectedPage} onChange={setSelectedPage} css={classes.tabs} />
        </div>
      )}
      {!called || loading ? (
        <Loader />
      ) : (
        <>
          {!isTestimonialsInfoFormHidden && (
            <Form
              onSubmit={handleSubmit}
              initialValues={initialValues}
              render={({ handleSubmit, values }) => (
                <form onSubmit={handleSubmit} css={classes.container}>
                  <div>
                    <Typography variant="body2" css={classes.label}>
                      Title
                    </Typography>
                    <InputField name="title" validate={validateRequired} placeholder="Enter Title" fullWidth />
                  </div>
                  <div>
                    <Typography variant="body2" css={classes.label}>
                      Description
                    </Typography>
                    <InputField
                      name="description"
                      validate={validateRequired}
                      placeholder="Enter Description"
                      fullWidth
                    />
                  </div>
                  <Button
                    variant="contained"
                    type="submit"
                    color="blue"
                    size="large"
                    fullWidth
                    disabled={!values?.title || !values?.description}
                  >
                    {createLoading || updateLoading ? '...Loading' : ' Save Edits'}
                  </Button>
                </form>
              )}
            />
          )}
          {testimonialInfo?.textFeedback?.length === 0 && (
            <Typography variant="body2" css={classes.warning}>
              You need to add at least one testimonial to see it on the selected page
            </Typography>
          )}
          {testimonialInfo?.textFeedback && (
            <TestimonialsCards
              testimonial={testimonialInfo}
              selectedPageId={selectedPage.id as CMSBoosterPages}
              platformId={platform.id}
              platformQuery={platformQuery}
            />
          )}
        </>
      )}
    </>
  )
}

export default withPlatformCheck(Testimonials)
